import { readableOn, errorWords } from "@alm/domain";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { RowActions } from "@/components/app/patterns/row-actions";
import { RecordCard } from "@/components/app/lists/record-card";
import { EmptyRows } from "@/components/app/lists/empty-rows";
import { RecordRow } from "@/components/app/lists/record-row";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { orpc, orpcClient, type Output } from "@/lib/orpc";
import { useCan, useCanOrganizationWide } from "@/lib/policy";
import { guardRoute } from "@/lib/policy/route-guard";
import { RowsSkeleton } from "@/routes/_authenticated/_administration/settings/-components/rows-skeleton";
import {
  TransactionTagForm,
  type TransactionTagFormValues,
} from "./-components/billing/transaction-tag-form";

export const Route = createFileRoute("/_authenticated/_administration/settings/transaction-tags")({
  beforeLoad: ({ context }) => guardRoute(context.me, "transaction-tags"),
  component: TransactionTagsSettings,
});

type ExpenseTag = Output<typeof orpcClient.financials.expenseTags.list>[number];
type Editing = { mode: "create" } | { mode: "edit"; tag: ExpenseTag };

const fail = (error: Error) => toast.error(errorWords(error));

const SHARED =
  " Every building shares them, so only someone who manages the books for the whole organization can change them.";

/** The labels a bookkeeper puts across expense entries, cutting the ledger a second way. */
function TransactionTagsSettings() {
  // Tags are every building's, so only the organization's bookkeepers change them.
  const canManage = useCanOrganizationWide("financial:manage");
  const keepsBooksHere = useCan("financial:manage");
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Editing | null>(null);
  const [deactivating, setDeactivating] = useState<ExpenseTag | null>(null);

  const tags = useQuery(orpc.financials.expenseTags.list.queryOptions({}));
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: orpc.financials.expenseTags.key() });

  const create = useMutation(
    orpc.financials.expenseTags.create.mutationOptions({
      onSuccess: async () => {
        toast.success("Tag created");
        setEditing(null);
        await invalidate();
      },
      onError: fail,
    }),
  );
  const update = useMutation(
    orpc.financials.expenseTags.update.mutationOptions({
      onSuccess: async () => {
        toast.success("Tag updated");
        setEditing(null);
        await invalidate();
      },
      onError: fail,
    }),
  );
  const deactivate = useMutation(
    orpc.financials.expenseTags.deactivate.mutationOptions({
      onSuccess: async () => {
        toast.success("Tag deactivated. Entries already carrying it keep it.");
        setDeactivating(null);
        await invalidate();
      },
      onError: fail,
    }),
  );

  const submit = (values: TransactionTagFormValues) => {
    if (editing?.mode === "edit") {
      update.mutate({ ...values, tagId: editing.tag.id });
      return;
    }
    create.mutate(values);
  };

  return (
    <>
      <PageHeader
        title="Transaction tags"
        meta={`Labels you put on expense entries to follow spending across categories, like a roof repair or a move-in special.${
          keepsBooksHere && !canManage ? SHARED : ""
        }`}
      />

      <RecordCard
        title="Tags"
        count={tags.data ? tags.data.length : undefined}
        canWrite={canManage}
        addLabel="Add tag"
        onAdd={() => setEditing({ mode: "create" })}
      >
        {tags.isPending ? (
          <RowsSkeleton />
        ) : tags.isError ? (
          <ReadFailed
            message="The transaction tags could not be loaded."
            error={tags.error}
            retrying={tags.isFetching}
            onRetry={() => void tags.refetch()}
          />
        ) : !tags.data.length ? (
          <EmptyRows text="No transaction tags yet. Add one to total the spending on a project that crosses categories, like Roof repair." />
        ) : (
          <ul className="divide-y">
            {tags.data.map((tag) => (
              <RecordRow
                key={tag.id}
                title={
                  <span className="flex items-center gap-2">
                    <span
                      className="inline-flex h-5 items-center rounded-xs px-1.5 text-xs font-medium"
                      style={{ backgroundColor: tag.color, color: readableOn(tag.color) }}
                    >
                      {tag.name}
                    </span>
                    {tag.isActive ? null : <Badge variant="outline">Inactive</Badge>}
                  </span>
                }
                meta={tag.description}
                actions={
                  canManage ? (
                    <RowActions
                      modal={false}
                      label={tag.name}
                      primary={{ label: "Edit", onSelect: () => setEditing({ mode: "edit", tag }) }}
                      items={[
                        tag.isActive
                          ? { label: "Deactivate tag", onSelect: () => setDeactivating(tag) }
                          : {
                              label: "Reactivate tag",
                              onSelect: () => update.mutate({ tagId: tag.id, isActive: true }),
                            },
                      ]}
                    />
                  ) : null
                }
              />
            ))}
          </ul>
        )}
      </RecordCard>

      <Dialog open={editing !== null} onOpenChange={(open) => (open ? null : setEditing(null))}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editing?.mode === "edit" ? "Edit tag" : "Add tag"}</DialogTitle>
            <DialogDescription>
              Tags cut across expense categories: one entry can carry several.
            </DialogDescription>
          </DialogHeader>
          {editing ? (
            <TransactionTagForm
              key={editing.mode === "edit" ? editing.tag.id : "new"}
              pending={create.isPending || update.isPending}
              submitLabel={editing.mode === "edit" ? "Save tag" : "Add tag"}
              defaultValues={
                editing.mode === "edit"
                  ? {
                      name: editing.tag.name,
                      color: editing.tag.color,
                      description: editing.tag.description ?? "",
                    }
                  : undefined
              }
              onSubmit={submit}
              onCancel={() => setEditing(null)}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={deactivating !== null}
        onOpenChange={(open) => (open ? null : setDeactivating(null))}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate &ldquo;{deactivating?.name}&rdquo;?</AlertDialogTitle>
            <AlertDialogDescription>
              It stops being offered on new entries. Expense entries already tagged with it keep the
              tag, so past reports do not change.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deactivating && deactivate.mutate({ tagId: deactivating.id })}
            >
              Deactivate tag
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
